import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { MAX_PAGINATION_LIMIT } from '../../graphql/paginationLimit'

export type ItemsPerPageOptions = 20 | 50 | 100

export const ITEMS_PER_PAGE_STORAGE_KEY = 'itemsPerPage'

type PreferencesState = {
  itemsPerPage: ItemsPerPageOptions
  userInfoGroup: string
}

type PreferencesSlicer = {
  readonly setItemsPerPage: (state: PreferencesState, action: PayloadAction<number>) => void
  readonly setUserInfoGroup: (state: PreferencesState, action: PayloadAction<string>) => void
}

export function normalizeItemsPerPage(value: number): ItemsPerPageOptions {
  if (!Number.isFinite(value) || value <= 20) {
    return 20
  }

  if (value <= 50) {
    return 50
  }

  return MAX_PAGINATION_LIMIT
}

export function readPersistedItemsPerPage(): ItemsPerPageOptions {
  try {
    const stored = localStorage.getItem(ITEMS_PER_PAGE_STORAGE_KEY)
    if (!stored) {
      return 20
    }

    return normalizeItemsPerPage(Number(stored))
  } catch {
    return 20
  }
}

export const preferencesSlice = createSlice<PreferencesState, PreferencesSlicer>({
  name: 'preferences',
  initialState: {
    itemsPerPage: readPersistedItemsPerPage(),
    userInfoGroup: '',
  },
  reducers: {
    setItemsPerPage: (state, action) => {
      const normalizedItemsPerPage = normalizeItemsPerPage(action.payload)
      try {
        localStorage.setItem(ITEMS_PER_PAGE_STORAGE_KEY, normalizedItemsPerPage.toString())
      } catch {
        // Private browsing can block localStorage; keep the in-memory preference.
      }
      state.itemsPerPage = normalizedItemsPerPage
    },
    setUserInfoGroup(state, action) {
      state.userInfoGroup = action.payload
    },
  },
})

export const { setItemsPerPage, setUserInfoGroup } = preferencesSlice.actions
